import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

export const specialActionLayouts = ["medallion", "impact_band", "side_seal"] as const;
export type SpecialActionLayout = (typeof specialActionLayouts)[number];

const specialBrandActionInputSchema = z.object({
  brand_id: z.string().uuid(),
  tagline: z.string().trim().min(1).max(140),
  link_url: z.string().trim().max(500).nullable(),
  starts_at: z.string().datetime({ offset: true }),
  ends_at: z.string().datetime({ offset: true }).nullable(),
  is_active: z.boolean(),
  sort_order: z.number().int().min(0),
  card_layout: z.enum(specialActionLayouts),
  logo_background_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).nullable(),
  show_logo: z.boolean(),
  show_seal: z.boolean(),
  show_brand_lines: z.boolean(),
  show_donation: z.boolean(),
  show_brand_name: z.boolean(),
  show_tagline: z.boolean(),
  show_end_date: z.boolean(),
  show_button: z.boolean(),
});

export interface SpecialBrandAction {
  id: string;
  brand_id: string;
  tagline: string;
  link_url: string | null;
  starts_at: string;
  ends_at: string | null;
  is_active: boolean;
  sort_order: number;
  card_layout: SpecialActionLayout;
  logo_background_color: string | null;
  show_logo: boolean;
  show_seal: boolean;
  show_brand_lines: boolean;
  show_donation: boolean;
  show_brand_name: boolean;
  show_tagline: boolean;
  show_end_date: boolean;
  show_button: boolean;
  created_at: string;
  updated_at: string;
  brand: {
    id: string;
    name: string;
    slug: string;
    logo_url: string | null;
    donation_percentage: number | null;
    commission_type: string | null;
    is_active: boolean | null;
    deeplink_inactive: boolean | null;
    merged_into_id: string | null;
  } | null;
}

export type SpecialBrandActionInput = Pick<
  SpecialBrandAction,
  "brand_id" | "tagline" | "link_url" | "starts_at" | "ends_at" | "is_active" | "sort_order" | "card_layout" |
  "logo_background_color" | "show_logo" | "show_seal" | "show_brand_lines" | "show_donation" |
  "show_brand_name" | "show_tagline" | "show_end_date" | "show_button"
>;

const actionSelect = `
  id, brand_id, tagline, link_url, starts_at, ends_at, is_active, sort_order, card_layout,
  logo_background_color, show_logo, show_seal, show_brand_lines, show_donation, show_brand_name,
  show_tagline, show_end_date, show_button, created_at, updated_at,
  brand:brands(id, name, slug, logo_url, donation_percentage, commission_type, is_active, deeplink_inactive, merged_into_id)
`;

export function useActiveSpecialBrandActions() {
  return useQuery({
    queryKey: ["special-brand-actions", "public"],
    staleTime: 60_000,
    queryFn: async () => {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from("special_brand_actions")
        .select(actionSelect)
        .eq("is_active", true)
        .lte("starts_at", now)
        .or(`ends_at.is.null,ends_at.gte.${now}`)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return ((data ?? []) as unknown as SpecialBrandAction[]).filter(
        (action) =>
          action.brand?.is_active &&
          !action.brand.deeplink_inactive &&
          !action.brand.merged_into_id,
      );
    },
  });
}

export function useAllSpecialBrandActions() {
  return useQuery({
    queryKey: ["special-brand-actions", "admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("special_brand_actions")
        .select(actionSelect)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as SpecialBrandAction[];
    },
  });
}

export function useCreateSpecialBrandAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SpecialBrandActionInput) => {
      specialBrandActionInputSchema.parse(input);
      const { error } = await supabase.from("special_brand_actions").insert(input);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["special-brand-actions"] }),
  });
}

export function useUpdateSpecialBrandAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<SpecialBrandActionInput> & { id: string }) => {
      specialBrandActionInputSchema.partial().parse(updates);
      const { error } = await supabase.from("special_brand_actions").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["special-brand-actions"] }),
  });
}

export function useDeleteSpecialBrandAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("special_brand_actions").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["special-brand-actions"] }),
  });
}